import CloseIcon from '@mui/icons-material/Close';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, Typography, useTheme, useMediaQuery, Alert } from '@mui/material';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import React, { useRef, useState } from 'react';
import { useLiveRegion } from '@schamburg-web/components';
import ReactGA from 'react-ga4';

interface AudioPlayerProps {
  src: string;
  className?: string;
  onClose?: () => void;
  onError?: (error: string | null) => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  className,
  onClose,
  onError,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { setMessage: setLiveRegionMessage } = useLiveRegion();

  // Notify parent component of error changes
  React.useEffect(() => {
    onError?.(error);
  }, [error, onError]);

  const handlePlay = () => {
    if (audioRef.current) {
      audioRef.current.play().catch((err) => {
        setError('Failed to play audio');
        setLiveRegionMessage('Audio playback failed');
      });
    }
  };

  // Set isPlaying to true when audio actually starts playing (autoplay or manual)
  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => {
      setIsPlaying(true);
      setError(null);
      setLiveRegionMessage('Audio playing');
    };

    const onError = () => {
      setError('Failed to load audio');
      setLiveRegionMessage('Audio failed to load');
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('error', onError);
    };
  }, [setLiveRegionMessage]);

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setLiveRegionMessage('Audio paused');
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      if (isPlaying) {
        handlePause();
      } else {
        handlePlay();
      }
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      if (audioRef.current) {
        const seekAmount = event.key === 'ArrowLeft' ? -10 : 10;
        const newTime = Math.max(0, Math.min(duration, progress + seekAmount));
        audioRef.current.currentTime = newTime;
        setProgress(newTime);
        setLiveRegionMessage(`Seeked to ${formatTime(newTime)}`);
      }
    }
  };

  // Helper to format seconds as mm:ss
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setLiveRegionMessage('Audio playback completed');
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  return (
    <Box
      className={className}
      role="region"
      aria-label="Traffic Report Audio Player"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        outline: 'none',
        '&:focus-visible': {
          outline: '2px solid #1976d2',
          outlineOffset: '2px',
          borderRadius: '4px',
        },
      }}
    >
      {isPlaying ? (
        <IconButton
          onClick={handlePause}
          size="small"
          sx={{ color: 'inherit' }}
          aria-label="Pause traffic report"
          aria-describedby="audio-status"
        >
          <PauseIcon />
        </IconButton>
      ) : (
        <IconButton
          onClick={handlePlay}
          size="small"
          sx={{ color: 'inherit' }}
          aria-label="Play traffic report"
          aria-describedby="audio-status"
        >
          <PlayArrowIcon />
        </IconButton>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, ml: 1 }}>
        <Typography
          id="audio-status"
          variant="button"
          sx={{ lineHeight: 1, fontSize: '0.875rem' }}
          aria-label={`Audio progress: ${formatTime(progress)} of ${
            duration ? formatTime(duration) : '0:00'
          }`}
        >
          {`${formatTime(progress)} / ${
            duration ? formatTime(duration) : '0:00'
          }`}
        </Typography>
      </Box>

      {onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'inherit' }}
          aria-label="Close audio player"
        >
          <CloseIcon />
        </IconButton>
      )}

      <audio
        ref={audioRef}
        src={src}
        aria-label="Traffic report audio"
        aria-describedby="audio-help"
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        style={{ display: 'none' }}
        autoPlay={true}
      />

      {/* Hidden help text for screen readers */}
      <Typography
        id="audio-help"
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        Use spacebar to play or pause, left and right arrow keys to seek
        backward and forward by 10 seconds
      </Typography>
    </Box>
  );
};

// Wrapper component with button to open AudioPlayer with slide animation
const AudioPlayerWithButton: React.FC<AudioPlayerProps> = ({
  src,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row', // Column on mobile, row on desktop
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'flex-start', // Center on mobile, left-align on desktop
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
      role="region"
      aria-label="Audio player"
      id="audio-player-region"
      aria-live="polite"
    >
      <Button
        title="Open traffic report audio player"
        aria-label={
          open
            ? 'Close traffic report audio player'
            : 'Open traffic report audio player'
        }
        aria-describedby="audio-player-help"
        aria-expanded={open}
        aria-controls="audio-player-region"
        onClick={() => {
          setOpen((v) => !v);
          ReactGA.event({
            category: 'Audio Player',
            action: open ? 'Close Audio Player' : 'Open Audio Player',
            label: 'Listen Button',
          });
        }}
        sx={{
          maxHeight: 40,
          paddingRight: 3,
          paddingLeft: 3,
          textAlign: 'center',
          color: 'inherit',
          width: isMobile ? 'auto' : '100px', // Flexible width on mobile
          minWidth: isMobile ? '80px' : '100px', // Ensure minimum width
          flexShrink: 0,
        }}
      >
        <Typography variant="button">Listen</Typography>
      </Button>

      {/* Hidden help text */}
      <Typography
        id="audio-player-help"
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        Click to {open ? 'close' : 'open'} the traffic report audio player
      </Typography>

      <Fade
        in={open}
        style={{ transformOrigin: '0 0 0' }}
        timeout={1000}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row', // Row on desktop to place error to the right
            alignItems: 'center', //
            minHeight: 40, // Match button height
            width: isMobile ? '100%' : 'auto', // Full width on mobile
            justifyContent: isMobile ? 'center' : 'flex-start', // Center audio player on mobile
            gap: isMobile ? 1 : 2, // Space between controls and error message
          }}
        >
          <Box
            sx={{
              minWidth: '150px', // Increased minimum width for audio controls
              flexShrink: 0, // Prevent the audio player from shrinking
              display: 'flex',
              justifyContent: 'center',
              mt: 0,
            }}
          >
            <AudioPlayer
              src={src}
              className={className}
              onClose={() => setOpen(false)}
              onError={setError}
            />
          </Box>

          {/* Error message positioned to the right of controls on desktop, below on mobile */}
          {error && (
            <Box
              sx={{
                flexShrink: 0, // Don't shrink the error message
                maxWidth: '100%', // Limit width on desktop
                pl: isMobile ? 1 : 0,
                pr: isMobile ? 1 : 0,
              }}
            >
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  fontSize: '0.7rem',
                  minHeight: 'auto',
                }}
              >
                {error}
              </Alert>
            </Box>
          )}
        </Box>
      </Fade>
    </Box>
  );
};

export default AudioPlayerWithButton;
